/**
 * 営業日計算エンジン
 * 
 * 設計方針:
 * - Shopify CDNからJSONを取得（APIコール不要、サーバー負荷なし）
 * - ブラウザセッション中はキャッシュを保持
 * - 管理画面で変更時、JSONが即時更新される
 * 
 * 使用方法:
 * - window.businessDayCalculator.calculateDeliveryDate('5営業日')
 * - window.businessDayCalculator.addBusinessDays(new Date(), 5)
 * - window.businessDayCalculator.isBusinessDay(new Date())
 */

(function() {
  'use strict';

  class BusinessDayCalculator {
    constructor() {
      this.holidays = new Map();
      this.settings = {
        closedDays: ['saturday', 'sunday'],
        includeNationalHolidays: true,
        cutoffTime: '12:00',
      };
      this.loaded = false;
      this.loading = false;
      this.loadPromise = null;
      
      // 日本の祝日 + 年末年始休業（フォールバック用）
      this.nationalHolidays = {
        // 2024-2025年末年始
        '2024-12-29': '年末休業',
        '2024-12-30': '年末休業',
        '2024-12-31': '年末休業',
        '2025-01-01': '元日',
        '2025-01-02': '年始休業',
        '2025-01-03': '年始休業',
        '2025-01-13': '成人の日',
        '2025-02-11': '建国記念の日',
        '2025-02-23': '天皇誕生日',
        '2025-02-24': '振替休日',
        '2025-03-20': '春分の日',
        '2025-04-29': '昭和の日',
        '2025-05-03': '憲法記念日',
        '2025-05-04': 'みどりの日',
        '2025-05-05': 'こどもの日',
        '2025-05-06': '振替休日',
        '2025-07-21': '海の日',
        '2025-08-11': '山の日',
        '2025-09-15': '敬老の日',
        '2025-09-23': '秋分の日',
        '2025-10-13': 'スポーツの日',
        '2025-11-03': '文化の日',
        '2025-11-23': '勤労感謝の日',
        '2025-11-24': '振替休日',
        // 2025-2026年末年始
        '2025-12-29': '年末休業',
        '2025-12-30': '年末休業',
        '2025-12-31': '年末休業',
        '2026-01-01': '元日',
        '2026-01-02': '年始休業',
        '2026-01-03': '年始休業',
        '2026-01-12': '成人の日',
        '2026-02-11': '建国記念の日',
        '2026-02-23': '天皇誕生日',
        '2026-03-20': '春分の日',
        '2026-04-29': '昭和の日',
        '2026-05-03': '憲法記念日',
        '2026-05-04': 'みどりの日',
        '2026-05-05': 'こどもの日',
        '2026-05-06': '振替休日',
        '2026-07-20': '海の日',
        '2026-08-11': '山の日',
        '2026-09-21': '敬老の日',
        '2026-09-22': '国民の休日',
        '2026-09-23': '秋分の日',
        '2026-10-12': 'スポーツの日',
        '2026-11-03': '文化の日',
        '2026-11-23': '勤労感謝の日',
      };
      
    }
    
    /**
     * 休日データを読み込み（CDNから）
     * @returns {Promise<void>}
     */
    async loadHolidays() {
      // 既にロード済み
      if (this.loaded) {
        return;
      }
      
      // ロード中の場合は待機
      if (this.loading && this.loadPromise) {
        return this.loadPromise;
      }
      
      this.loading = true;
      this.loadPromise = this._doLoadHolidays();
      
      try {
        await this.loadPromise;
      } finally {
        this.loading = false;
      }
    }
    
    async _doLoadHolidays() {
      try {
        // LiquidからMetafieldデータを使用（shop.metafields.custom.business_calendar）
        const data = window.businessCalendarData;

        if (data && typeof data === 'object') {
          // 休日データを設定
          if (data.holidays) {
            this.holidays.clear();
            Object.entries(data.holidays).forEach(([date, info]) => {
              this.holidays.set(date, info);
            });
          }
          
          // 設定を適用
          if (data.settings) {
            this.settings = { ...this.settings, ...data.settings };
          }
          
          // 祝日データが少ない場合はフォールバックの祝日を追加
          const nationalHolidaysCount = Array.from(this.holidays.values()).filter(h => h.type === 'national_holiday').length;
          if (nationalHolidaysCount < 10) {
            this._mergeNationalHolidays();
          }
          
        } else {
          console.warn('[BusinessDayCalculator] No data available, using fallback');
          this._loadFallback();
        }
      } catch (error) {
        console.warn('[BusinessDayCalculator] Error loading data:', error);
        this._loadFallback();
      }
      
      this.loaded = true;
    }
    
    /**
     * フォールバック（CDN取得失敗時）
     */
    _loadFallback() {
      // 日本の祝日をデフォルトで設定
      this.holidays.clear();
      Object.entries(this.nationalHolidays).forEach(([date, name]) => {
        this.holidays.set(date, { type: 'national_holiday', name });
      });
      
    }
    
    /**
     * 祝日データをマージ（既存データに祝日を追加）
     */
    _mergeNationalHolidays() {
      let addedCount = 0;
      Object.entries(this.nationalHolidays).forEach(([date, name]) => {
        // 既に登録されていない場合のみ追加
        if (!this.holidays.has(date)) {
          const type = name.includes('休業') ? 'company_holiday' : 'national_holiday';
          this.holidays.set(date, { type, name });
          addedCount++;
        }
      });
    }
    
    /**
     * 日付をYYYY-MM-DD形式にフォーマット
     * @param {Date} date
     * @returns {string}
     */
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    /**
     * 日付を日本語形式にフォーマット
     * @param {Date} date
     * @returns {string}
     */
    formatDateJP(date) {
      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const weekday = weekdays[date.getDay()];
      return `${year}/${month}/${day}(${weekday})`;
    }
    
    /**
     * 指定日が営業日かどうか
     * @param {Date} date
     * @returns {boolean}
     */
    isBusinessDay(date) {
      const dateStr = this.formatDate(date);
      const dayOfWeek = date.getDay();
      
      // 土日チェック
      if (this.settings.closedDays.includes('saturday') && dayOfWeek === 6) {
        return false;
      }
      if (this.settings.closedDays.includes('sunday') && dayOfWeek === 0) {
        return false;
      }
      
      // 休日チェック
      if (this.holidays.has(dateStr)) {
        return false;
      }
      
      return true;
    }
    
    /**
     * 営業日を加算（土日祝・会社休業日を除外）
     * @param {Date} startDate - 開始日
     * @param {number} days - 加算する営業日数
     * @returns {Date} 結果の日付
     */
    addBusinessDays(startDate, days) {
      const result = new Date(startDate);
      let remaining = days;
      
      // 0営業日の場合は当日を返す
      if (days === 0) {
        return result;
      }
      
      while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        if (this.isBusinessDay(result)) {
          remaining--;
        }
      }
      
      return result;
    }
    
    /**
     * 指定日が全商品休業日（isGlobalClosure）かどうか
     * @param {Date} date
     * @returns {boolean}
     */
    isGlobalClosureDay(date) {
      const dateStr = this.formatDate(date);
      const holiday = this.holidays.get(dateStr);
      return holiday?.isGlobalClosure === true;
    }
    
    /**
     * 暦日を加算（全商品休業日 isGlobalClosure はスキップ）
     * 土日祝は含むが、全商品休業日（工場完全休業など）はスキップ
     * @param {Date} startDate - 開始日
     * @param {number} days - 加算する日数
     * @returns {Date} 結果の日付
     */
    addCalendarDays(startDate, days) {
      const result = new Date(startDate);
      let remaining = days;
      
      // 0日の場合は当日を返す
      if (days === 0) {
        return result;
      }
      
      while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        
        // 全商品休業日（isGlobalClosure）はスキップ
        if (this.isGlobalClosureDay(result)) {
          continue;  // カウントせずに次の日へ
        }
        
        remaining--;
      }
      
      return result;
    }
    
    /**
     * 締切時間を考慮した開始日を取得
     * @param {Date} [baseDate] - 基準日（省略時は現在時刻）
     * @returns {Date} 計算開始日
     */
    getStartDate(baseDate) {
      const now = baseDate || new Date();
      const cutoffTime = this.settings.cutoffTime || '12:00';
      
      // 締切時間をパース（HH:MM形式）
      const [cutoffHour, cutoffMinute] = cutoffTime.split(':').map(n => parseInt(n, 10));
      
      // 締切時刻のDateオブジェクトを作成
      const cutoff = new Date(now);
      cutoff.setHours(cutoffHour, cutoffMinute, 0, 0);
      
      // 現在時刻が締切時刻を過ぎている場合は翌営業日から開始
      if (now > cutoff) {
        // 翌日から開始
        const nextDay = new Date(now);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);
        
        // 翌日が休業日の場合は、次の営業日まで進める
        while (!this.isBusinessDay(nextDay)) {
          nextDay.setDate(nextDay.getDate() + 1);
        }
        
        return nextDay;
      }
      
      // 締切前の場合は当日から
      return now;
    }
    
    /**
     * 現在時刻が締切時間を過ぎているかチェック
     * @param {Date} [checkTime] - チェックする時刻（省略時は現在時刻）
     * @returns {boolean}
     */
    isPastCutoffTime(checkTime) {
      const now = checkTime || new Date();
      const cutoffTime = this.settings.cutoffTime || '12:00';
      const [cutoffHour, cutoffMinute] = cutoffTime.split(':').map(n => parseInt(n, 10));
      
      const cutoff = new Date(now);
      cutoff.setHours(cutoffHour, cutoffMinute, 0, 0);
      
      return now > cutoff;
    }

    /**
     * 発送予定日を計算（納期文字列または数値から）
     * @param {string|number} deliveryInput - "5営業日", "5日", または数値
     * @param {number} [leadTimeAdjustment=0] - 追加の日数
     * @param {object} [options={}] - オプション { calcType: 'business_days' | 'calendar_days' }
     * @returns {{date: Date, formatted: string, days: number, useBusinessDays: boolean}}
     */
    calculateDeliveryDate(deliveryInput, leadTimeAdjustment = 0, options = {}) {
      let days = 0;
      let useBusinessDays = true;  // デフォルトは営業日計算
      
      // 数値の場合
      if (typeof deliveryInput === 'number') {
        days = deliveryInput;
        // オプションで計算タイプが指定されている場合はそちらを使用
        useBusinessDays = options.calcType !== 'calendar_days';
      }
      // 文字列の場合
      else if (typeof deliveryInput === 'string') {
        // 当日発送
        if (deliveryInput.includes('当日')) {
          days = 0;
          useBusinessDays = true;
        }
        // 翌営業日
        else if (deliveryInput.includes('翌営業日')) {
          days = 1;
          useBusinessDays = true;
        }
        // "5営業日" → 日数部分のみ抽出。計算方式は options.calcType を優先し、
        // 未指定時のみ従来どおり営業日計算（後方互換）
        else {
          const businessMatch = deliveryInput.match(/(\d+)営業日/);
          if (businessMatch) {
            days = parseInt(businessMatch[1], 10);
            if (options.calcType) {
              // 商品メタフィールド（printhub.delivery_calc_type）の設定を尊重する。
              // 価格マトリックス上は "N営業日" 形式で保持されていても、
              // 商品が暦日計算設定なら暦日で計算する。
              useBusinessDays = options.calcType !== 'calendar_days';
            } else {
              useBusinessDays = true;
            }
          }
          // "5日" → options.calcTypeが指定されていればそちらを優先、なければ暦日計算
          else {
            const calendarMatch = deliveryInput.match(/(\d+)日/);
            if (calendarMatch) {
              days = parseInt(calendarMatch[1], 10);
              // options.calcTypeが明示的に指定されていればそちらを優先
              if (options.calcType) {
                useBusinessDays = options.calcType !== 'calendar_days';
              } else {
                // 指定がなければ暦日計算（後方互換性）
                useBusinessDays = false;
              }
            }
          }
        }
      }
      
      // 納期調整を加算
      days += leadTimeAdjustment;
      
      // 締切時間を考慮した開始日を取得
      const startDate = this.getStartDate();
      let targetDate;
      
      if (useBusinessDays) {
        targetDate = this.addBusinessDays(startDate, days);
      } else {
        targetDate = this.addCalendarDays(startDate, days);
      }
      
      return {
        date: targetDate,
        formatted: this.formatDateJP(targetDate),
        days: days,
        useBusinessDays: useBusinessDays,
        // 後方互換性のために残す
        businessDays: days,
      };
    }
    
    /**
     * 休日情報を取得
     * @param {string} dateStr - YYYY-MM-DD形式の日付
     * @returns {object|null}
     */
    getHolidayInfo(dateStr) {
      return this.holidays.get(dateStr) || null;
    }
    
    /**
     * 指定期間の休日一覧を取得
     * @param {Date} fromDate
     * @param {Date} toDate
     * @returns {Array}
     */
    getHolidaysInRange(fromDate, toDate) {
      const result = [];
      const current = new Date(fromDate);
      
      while (current <= toDate) {
        const dateStr = this.formatDate(current);
        const holiday = this.holidays.get(dateStr);
        
        if (holiday) {
          result.push({ date: dateStr, ...holiday });
        }
        
        current.setDate(current.getDate() + 1);
      }
      
      return result;
    }
    
    /**
     * 設定を再読み込み（デバッグ用）
     * @returns {Promise<void>}
     */
    async reloadSettings() {
      this.loaded = false;
      this.loading = false;
      this.loadPromise = null;
      await this.loadHolidays();
    }
  }
  
  // グローバルインスタンスを作成
  window.businessDayCalculator = new BusinessDayCalculator();
  
  // 自動初期化（DOMContentLoaded時にロード）
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.businessDayCalculator.loadHolidays();
    });
  } else {
    // 既にDOMがロード済み
    window.businessDayCalculator.loadHolidays();
  }
  
})();

